import { cn } from '@/lib/utils';

interface SparkPoint {
  value: number;
}

interface KpiTileProps {
  label: string;
  value: string | number;
  subtext?: string;
  loading?: boolean;
  onClick?: () => void;
  spark?: SparkPoint[];
  /** 'ok' | 'warn' | 'crit' — colors the big number */
  tone?: 'ok' | 'warn' | 'crit' | 'neutral';
}

function Sparkline({ points }: { points: SparkPoint[] }) {
  if (points.length < 2) return null;

  const vals = points.map((p) => p.value);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;

  const W = 64;
  const H = 20;
  const step = W / (vals.length - 1);

  const coords = vals.map((v, i) => ({
    x: i * step,
    y: H - ((v - min) / range) * H,
  }));

  const polyline = coords.map((c) => `${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' ');
  const last = coords[coords.length - 1];

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="overflow-visible">
      <polyline
        points={polyline}
        fill="none"
        stroke="rgba(201,169,138,0.5)"
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={last.x} cy={last.y} r="2" fill="#C9A98A" />
    </svg>
  );
}

// Dark-theme inks: tiles always sit on the dark green instrument strip
const toneColor: Record<string, string> = {
  ok: '#4ADE80',
  warn: '#E8B45A',
  crit: '#E0635C',
  neutral: '#F0EEE6',
};

export function KpiTile({ label, value, subtext, loading, onClick, spark, tone = 'neutral' }: KpiTileProps) {
  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
      className={cn(
        'cmd-panel cmd-panel-inner cmd-dark rounded p-3 flex flex-col gap-1 select-none',
        onClick && 'cmd-tile',
      )}
    >
      {/* Micro-label */}
      <span className="cmd-label truncate">{label}</span>

      {/* Big value */}
      {loading ? (
        <div className="h-8 w-20 rounded animate-pulse bg-white/5 mt-1" />
      ) : (
        <span
          className="text-[28px] leading-none font-mono tabular-nums font-semibold mt-1"
          style={{ color: toneColor[tone] }}
        >
          {value}
        </span>
      )}

      {/* Subtext + sparkline row */}
      <div className="flex items-end justify-between mt-0.5">
        {subtext && (
          <span className="cmd-label text-[9px] leading-tight max-w-[70%]">{subtext}</span>
        )}
        {spark && spark.length >= 2 && (
          <div className="shrink-0">
            <Sparkline points={spark} />
          </div>
        )}
      </div>
    </div>
  );
}
